import {
  GYM_MEMBER_LIMIT_DEFAULT,
  adminGymCreateSchema,
  type AdminGymCreate,
} from '@steado/admin-contract/gyms';

/**
 * The New gym form: a gym and its owner, set up by Steado after the gym has
 * written in. Owners cannot sign themselves up; the API emails the owner an
 * invite to set a password.
 */

export interface NewGymValues {
  readonly gymName: string;
  readonly ownerName: string;
  readonly ownerEmail: string;
  /** Optional; blank sends none. */
  readonly ownerPhone: string;
  readonly timeZone: string;
  /** As typed; the contract checks it once it is a number. */
  readonly memberLimit: string;
}

export type NewGymField = keyof NewGymValues;

/** India first, as the API's own list starts. */
export const DEFAULT_TIME_ZONE = 'Asia/Kolkata';

export const EMPTY_NEW_GYM: NewGymValues = {
  gymName: '',
  ownerName: '',
  ownerEmail: '',
  ownerPhone: '',
  timeZone: DEFAULT_TIME_ZONE,
  memberLimit: String(GYM_MEMBER_LIMIT_DEFAULT),
};

export function toCreateBody(values: NewGymValues): AdminGymCreate {
  const phone = values.ownerPhone.replace(/[\s-]/g, '');
  return {
    gymName: values.gymName.trim(),
    ownerName: values.ownerName.trim(),
    ownerEmail: values.ownerEmail.trim(),
    ...(phone === '' ? {} : { ownerPhone: phone }),
    timeZone: values.timeZone,
    memberLimit: Number(values.memberLimit.trim()),
  };
}

/**
 * What is wrong with each field, by the contract the API validates against,
 * so the form refuses exactly what the server would. One message a field.
 */
export function fieldIssues(values: NewGymValues): Partial<Record<NewGymField, string>> {
  const issues: Partial<Record<NewGymField, string>> = {};
  // `Number('')` is 0 and `Number('2.5')` a fraction: say what to type, not what the parser saw.
  if (!/^\d+$/.test(values.memberLimit.trim())) issues.memberLimit = 'Type a whole number.';
  const result = adminGymCreateSchema.safeParse(toCreateBody(values));
  if (result.success) return issues;
  for (const issue of result.error.issues) {
    const field = issue.path[0];
    if (typeof field === 'string' && field in values) issues[field as NewGymField] ??= issue.message;
  }
  return issues;
}
